import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PanelsTopLeft, Zap, Sun, BadgeIndianRupee, Landmark, ArrowUpRight, Download } from "lucide-react";
import { generateDesignPdf } from "@/lib/generateDesignPdf";
import { SideView } from "@/components/SideView";
import { Row3D } from "@/components/Row3D";

const MODELS = [
  { name: "575W Bifacial N-Type", lenFt: 7.5, widFt: 3.7, watts: 575 },
  { name: "545W Mono PERC", lenFt: 7.4, widFt: 3.7, watts: 545 },
];

const TILTS = [12, 15, 18, 22, 30];
const SUN_ALT = 26; // winter 9 AM sun altitude for ~29°N (Muzaffarnagar) — worst case

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

const pct = (v, min, max) => ((v - min) / (max - min)) * 100;

const fmtPower = (kw) => (kw >= 1000 ? `${(kw / 1000).toFixed(2)} MW` : `${kw.toFixed(1)} kW`);

export const Designer = ({ onQuote }) => {
  const [len, setLen] = useState(40);
  const [wid, setWid] = useState(28);
  const [mi, setMi] = useState(0);
  const [tilt, setTilt] = useState(18);
  const [shadowSafe, setShadowSafe] = useState(true);
  const model = MODELS[mi];

  const geo = useMemo(() => {
    const tiltRad = (tilt * Math.PI) / 180;
    const sunRad = (SUN_ALT * Math.PI) / 180;
    const footprintFt = model.lenFt * Math.cos(tiltRad);
    const shadowFt = (model.lenFt * Math.sin(tiltRad)) / Math.tan(sunRad);
    const pitchFt = footprintFt + shadowFt;
    return { footprintFt, shadowFt, pitchFt };
  }, [model, tilt]);

  const r = useMemo(() => {
    const cols = Math.max(0, Math.floor((len - 3) / (model.widFt + 0.4)));
    const rows = shadowSafe
      ? Math.max(0, Math.floor((wid - 3) / geo.pitchFt))
      : Math.max(0, Math.floor((wid - 3) / (model.lenFt + 0.4)));
    const count = cols * rows;
    const kw = (count * model.watts) / 1000;
    const gen = kw * 115;
    const save = gen * 8 * 0.92;
    const subsidy = kw >= 3 ? 78000 : kw >= 2 ? 60000 : kw >= 1 ? 30000 : 0;
    return { cols, rows, count, kw, gen, save, subsidy };
  }, [len, wid, model, shadowSafe, geo]);

  const scale = Math.min(560 / len, 320 / wid);
  const roofW = Math.round(len * scale);
  const roofH = Math.round(wid * scale);

  const showTiles = r.count > 0 && r.count <= 250;
  const showBands = shadowSafe && showTiles;
  const pxPerFt = (roofH - 16) / ((r.rows - 1) * geo.pitchFt + geo.footprintFt);
  const bandH = geo.footprintFt * pxPerFt;
  const shadowH = geo.shadowFt * pxPerFt;

  const stats = [
    { icon: PanelsTopLeft, label: "Panels", value: `${r.count}`, tid: "designer-stat-panels" },
    { icon: Zap, label: "System size", value: fmtPower(r.kw), tid: "designer-stat-system" },
    { icon: Sun, label: "Monthly generation", value: `${Math.round(r.gen).toLocaleString("en-IN")} units`, tid: "designer-stat-generation" },
    { icon: BadgeIndianRupee, label: "Monthly savings", value: inr(r.save), tid: "designer-stat-savings" },
  ];

  const tile = (key) => (
    <motion.div
      key={key}
      initial={{ opacity: 0, scale: 0.4 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: Math.min(key * 0.012, 0.7), ease: [0.16, 1, 0.3, 1] }}
      className="rounded-[3px] border border-gold/25"
      style={{
        backgroundImage:
          "linear-gradient(rgba(245,158,11,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.09) 1px, transparent 1px), linear-gradient(135deg, #1B2836 0%, #0D1219 70%)",
        backgroundSize: "7px 7px, 7px 7px, 100% 100%",
      }}
    />
  );

  return (
    <section id="designer" className="relative overflow-hidden py-24 lg:py-32">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-[-8%] h-[460px] w-[460px] rounded-full bg-gold/10 blur-[120px]"
      />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <p className="eyebrow">03 — Panel Designer</p>
          <h2 className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.08]">
            See your roof <span className="text-gold-grad">fill with power.</span>
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-mutegray">
            Set your roof size, pick a panel and tilt angle, and watch a real layout appear — with
            shadow analysis built in. Send it to us and we'll turn it into an exact quote.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          {/* controls */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-[1.4rem] border border-white/10 bg-surface p-7 sm:p-8 lg:col-span-5"
          >
            <div data-testid="designer-length-wrap">
              <div className="flex items-baseline justify-between">
                <label htmlFor="des-len" className="font-mono text-[10px] tracking-[0.22em] uppercase text-mutegray">
                  Roof length
                </label>
                <span className="font-display text-xl font-bold text-gold" data-testid="designer-length-value">{len} ft</span>
              </div>
              <input
                id="des-len"
                type="range"
                min="10"
                max="1600"
                step="1"
                value={len}
                data-testid="designer-length-slider"
                onChange={(e) => setLen(Number(e.target.value))}
                className="gold-range mt-4"
                style={{ "--fill": `${pct(len, 10, 1600)}%` }}
              />
            </div>

            <div className="mt-8" data-testid="designer-width-wrap">
              <div className="flex items-baseline justify-between">
                <label htmlFor="des-wid" className="font-mono text-[10px] tracking-[0.22em] uppercase text-mutegray">
                  Roof width
                </label>
                <span className="font-display text-xl font-bold text-gold" data-testid="designer-width-value">{wid} ft</span>
              </div>
              <input
                id="des-wid"
                type="range"
                min="10"
                max="1000"
                step="1"
                value={wid}
                data-testid="designer-width-slider"
                onChange={(e) => setWid(Number(e.target.value))}
                className="gold-range mt-4"
                style={{ "--fill": `${pct(wid, 10, 1000)}%` }}
              />
            </div>

            <div className="mt-8">
              <label htmlFor="des-model" className="mb-2 block font-mono text-[10px] tracking-[0.22em] uppercase text-mutegray">
                Panel model
              </label>
              <select
                id="des-model"
                className="field"
                value={mi}
                data-testid="designer-panel-model"
                onChange={(e) => setMi(Number(e.target.value))}
              >
                {MODELS.map((m, i) => (
                  <option key={m.name} value={i}>{m.name}</option>
                ))}
              </select>
            </div>

            <div className="mt-6" data-testid="designer-tilt-wrap">
              <p className="mb-2 font-mono text-[10px] tracking-[0.22em] uppercase text-mutegray">Tilt angle</p>
              <div className="grid grid-cols-5 gap-2">
                {TILTS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`designer-tilt-${t}`}
                    onClick={() => setTilt(t)}
                    className={`rounded-xl border py-2.5 font-display text-sm font-bold transition-all duration-300 ${
                      tilt === t
                        ? "border-gold bg-gold text-ink shadow-[0_0_20px_rgba(245,158,11,0.35)]"
                        : "border-white/10 bg-ink/60 text-mutegray hover:border-gold/40 hover:text-gold"
                    }`}
                  >
                    {t}°
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between rounded-xl border border-white/10 bg-ink/60 px-4 py-3.5" data-testid="designer-shadow-toggle-wrap">
              <div className="pr-4">
                <p className="text-sm font-semibold text-warm">Shadow-safe row spacing</p>
                <p className="mt-0.5 text-[11px] leading-snug text-mutegray">
                  Space rows so no panel shades another at {tilt}° tilt
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={shadowSafe}
                aria-label="Toggle shadow-safe row spacing"
                data-testid="designer-shadow-toggle"
                onClick={() => setShadowSafe((s) => !s)}
                className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 ${shadowSafe ? "bg-gold" : "bg-white/15"}`}
              >
                <span
                  className={`absolute top-1 h-5 w-5 rounded-full transition-all duration-300 ${shadowSafe ? "left-6 bg-ink" : "left-1 bg-mutegray"}`}
                />
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-gold/25 bg-gold/[0.05] p-5" data-testid="designer-shadow-card">
              <div className="flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-gold">
                  <Sun className="h-3.5 w-3.5" /> Shadow analysis — {tilt}° tilt
                </p>
                <span
                  className={`rounded-full border px-3 py-1 font-mono text-[9px] tracking-[0.15em] ${
                    shadowSafe
                      ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
                      : "border-red-400/30 bg-red-500/10 text-red-300"
                  }`}
                  data-testid="designer-shadow-status"
                >
                  {shadowSafe ? "SAFE" : "SHADING RISK"}
                </span>
              </div>
              <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-ink/50 py-2.5">
                  <dt className="font-mono text-[8px] tracking-[0.14em] uppercase text-mutegray">Sun altitude*</dt>
                  <dd className="mt-1 font-display text-base font-bold text-warm" data-testid="designer-shadow-sun">{SUN_ALT}°</dd>
                </div>
                <div className="rounded-lg bg-ink/50 py-2.5">
                  <dt className="font-mono text-[8px] tracking-[0.14em] uppercase text-mutegray">Shadow / row</dt>
                  <dd className="mt-1 font-display text-base font-bold text-warm" data-testid="designer-shadow-length">{geo.shadowFt.toFixed(1)} ft</dd>
                </div>
                <div className="rounded-lg bg-ink/50 py-2.5">
                  <dt className="font-mono text-[8px] tracking-[0.14em] uppercase text-mutegray">Row pitch</dt>
                  <dd className="mt-1 font-display text-base font-bold text-warm" data-testid="designer-shadow-pitch">{geo.pitchFt.toFixed(1)} ft</dd>
                </div>
              </dl>
              <p className="mt-3 text-[11px] leading-relaxed text-mutegray">
                *Winter 9 AM sun at ~29°N (Muzaffarnagar) — worst case.{" "}
                {shadowSafe
                  ? `Rows are spaced ${geo.pitchFt.toFixed(1)} ft apart, so every panel stays in full sun all day.`
                  : "Dense rows will shade each other at this tilt — suitable only for flush-mounted low-tilt roofs."}
              </p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {stats.map((s) => (
                <div key={s.label} className="rounded-xl border border-white/10 bg-ink/60 px-4 py-3.5" data-testid={s.tid}>
                  <p className="flex items-center gap-2 font-mono text-[9px] tracking-[0.18em] uppercase text-mutegray">
                    <s.icon className="h-3.5 w-3.5 text-gold" /> {s.label}
                  </p>
                  <p className="mt-1.5 font-display text-lg font-bold text-warm">{s.value}</p>
                </div>
              ))}
              <div className="col-span-2 rounded-xl border border-gold/30 bg-gold/[0.07] px-4 py-3.5" data-testid="designer-stat-subsidy">
                <p className="flex items-center gap-2 font-mono text-[9px] tracking-[0.18em] uppercase text-gold">
                  <Landmark className="h-3.5 w-3.5" /> Govt subsidy on this system
                </p>
                <p className="mt-1.5 font-display text-xl font-bold text-gold-grad">{inr(r.subsidy)}</p>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <button
                type="button"
                data-testid="designer-cta-quote"
                onClick={() =>
                  onQuote(
                    `Panel Designer: ${len}×${wid} ft roof, ${r.count} × ${model.name} panels (${fmtPower(r.kw)}), ${tilt}° tilt, ${shadowSafe ? "shadow-safe spacing" : "dense layout"}. Please quote this design.`
                  )
                }
                className="group inline-flex flex-1 items-center justify-center gap-2.5 rounded-full bg-gold px-6 py-4 text-sm font-bold text-ink transition-all duration-300 hover:bg-goldlight hover:shadow-[0_0_40px_rgba(245,158,11,0.45)]"
              >
                Get this design as a free quote
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <button
                type="button"
                data-testid="designer-download-pdf"
                onClick={() => generateDesignPdf({ len, wid, model, tilt, pitchFt: geo.pitchFt, shadowFt: geo.shadowFt, shadowSafe, ...r })}
                aria-label="Download your roof design as PDF"
                className="inline-flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold transition-all duration-300 hover:bg-gold hover:text-ink hover:shadow-[0_0_28px_rgba(245,158,11,0.45)]"
              >
                <Download className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-center text-[11px] text-mutegray">
              Portrait layout with walkway margins — we optimise it further on site.
            </p>
          </motion.div>

          {/* canvas */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-[1.4rem] border border-white/10 bg-ink p-8 lg:col-span-7"
            data-testid="designer-canvas"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(245,158,11,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.05) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            />
            <span className="absolute top-5 right-5 z-[3] rounded-full border border-gold/40 bg-ink/70 px-3.5 py-1.5 font-mono text-[10px] tracking-[0.2em] text-gold backdrop-blur-md" data-testid="designer-tilt-badge">
              {tilt}° TILT
            </span>
            <div className="relative flex min-h-[300px] items-center justify-center py-2 sm:min-h-[380px]">
              <div className="relative">
                {r.count === 0 ? (
                  <div className="flex h-56 w-72 items-center justify-center rounded-xl border border-dashed border-white/20 sm:h-64 sm:w-96">
                    <p className="px-6 text-center text-sm text-mutegray" data-testid="designer-empty">
                      Roof too small for a panel row — increase the size.
                    </p>
                  </div>
                ) : showBands ? (
                  <div
                    className="rounded-lg border-2 border-gold/40 bg-[#10141b] p-2 shadow-[0_0_60px_rgba(245,158,11,0.15)]"
                    style={{ width: roofW, height: roofH }}
                    data-testid="designer-band-view"
                  >
                    {Array.from({ length: r.rows }).map((_, ri) => (
                      <div
                        key={ri}
                        className="absolute left-2 right-2"
                        style={{ top: 8 + ri * geo.pitchFt * pxPerFt, height: bandH }}
                      >
                        <div className="grid h-full gap-[3px]" style={{ gridTemplateColumns: `repeat(${r.cols}, 1fr)` }}>
                          {Array.from({ length: r.cols }).map((_, ci) => tile(ri * r.cols + ci))}
                        </div>
                        {ri < r.rows - 1 && (
                          <div
                            className="absolute left-0 right-0 overflow-hidden rounded-b-[4px] border-x border-b border-red-400/25 bg-red-500/15"
                            style={{ top: bandH, height: shadowH }}
                          >
                            {ri === 0 && (
                              <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[7px] tracking-[0.18em] text-red-300/90">
                                SHADOW {geo.shadowFt.toFixed(1)} FT
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : showTiles ? (
                  <div
                    className="grid gap-[3px] rounded-lg border-2 border-gold/40 bg-[#10141b] p-2 shadow-[0_0_60px_rgba(245,158,11,0.15)]"
                    style={{
                      width: roofW,
                      height: roofH,
                      gridTemplateColumns: `repeat(${r.cols}, 1fr)`,
                      gridTemplateRows: `repeat(${r.rows}, 1fr)`,
                    }}
                  >
                    {Array.from({ length: r.count }).map((_, i) => tile(i))}
                  </div>
                ) : (
                  <div
                    className="rounded-lg border-2 border-gold/40 bg-[#141c26] p-2 shadow-[0_0_60px_rgba(245,158,11,0.15)]"
                    style={{
                      width: roofW,
                      height: roofH,
                      backgroundImage:
                        "linear-gradient(rgba(245,158,11,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.16) 1px, transparent 1px)",
                      backgroundSize: `${(roofW - 16) / r.cols}px ${(roofH - 16) / r.rows}px`,
                      backgroundPosition: "8px 8px",
                    }}
                    data-testid="designer-pattern-view"
                  />
                )}
                <p className="mt-3 text-center font-mono text-[11px] tracking-[0.2em] text-gold" data-testid="designer-dim-length">
                  {len} FT
                </p>
                <p
                  className="absolute -left-7 top-0 h-full w-4 font-mono text-[11px] tracking-[0.2em] text-gold"
                  style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", display: "flex", alignItems: "center" }}
                  data-testid="designer-dim-width"
                >
                  {wid} FT
                </p>
              </div>
            </div>
            <div className="relative mt-4 flex items-center justify-between border-t border-white/10 pt-4">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-mutegray" data-testid="designer-canvas-caption">
                Live roof layout — {r.cols} × {r.rows} array{r.count > 250 ? " · pattern view" : ""}
              </p>
              <p className="font-display text-sm font-bold text-gold">{fmtPower(r.kw)}</p>
            </div>

            <div className="relative mt-6" data-testid="designer-sideview-wrap">
              <SideView
                tilt={tilt}
                footprintFt={geo.footprintFt}
                shadowFt={geo.shadowFt}
                pitchFt={geo.pitchFt}
                model={model}
                shadowSafe={shadowSafe}
              />
            </div>

            <div className="relative mt-6" data-testid="designer-3d-wrap">
              <Row3D tilt={tilt} shadowFt={geo.shadowFt} shadowSafe={shadowSafe} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Designer;
